import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";
import { isEmpty } from "lodash";
import numeral from "numeral";
import { Badge, Button, Col, Row, Table } from "react-bootstrap";
import format from "date-fns/format";

import { AgGridReact } from "@ag-grid-community/react";
import "@ag-grid-community/styles/ag-grid.css";
import "@ag-grid-community/styles/ag-theme-alpine.css";
import { ModuleRegistry } from "@ag-grid-community/core";
import { ClientSideRowModelModule } from "@ag-grid-community/client-side-row-model";
import { CsvExportModule } from "@ag-grid-community/csv-export";
import { goalIcons } from "../../../../components/common/sDgs";
import EditorNoTools from "../../../../hooks/EditorNoTools";

import "./styles.css";

import { ColumnsToolPanelModule } from "@ag-grid-enterprise/column-tool-panel";
import { FiltersToolPanelModule } from "@ag-grid-enterprise/filter-tool-panel";
import { SetFilterModule } from "@ag-grid-enterprise/set-filter";
import { RowGroupingModule } from "@ag-grid-enterprise/row-grouping";
import { ExcelExportModule } from "@ag-grid-enterprise/excel-export";

import { AG_GRID_LOCALE_MS } from "../../../../locales/locale";

ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  ColumnsToolPanelModule,
  RowGroupingModule,
  CsvExportModule,
  ExcelExportModule,
  FiltersToolPanelModule,
  SetFilterModule,
]);

const AnalysisTable = ({
  gridRef,
  onGridReady,
  rowData,
  setRowData,
  selectRmk,
  saveColumnState,
}) => {
  const quillRef1 = useRef(null);
  const quillRef2 = useRef(null);
  const quillRef3 = useRef(null);
  const quillRef4 = useRef(null);
  const quillRef5 = useRef(null);

  const [years, setYears] = useState([]);

  const { projects } = useSelector((state) => ({
    projects: state.analysisTableReducer.projectApplications,
  }));

  const containerStyle = useMemo(() => ({ width: "100%", height: "100%" }), []);
  const gridStyle = useMemo(() => ({ height: "85vh", width: "100%" }), []);

  const colDefs = useMemo(() => {
    return [
      {
        headerName: "#",
        valueGetter: "node.rowIndex + 1",
      },
      // {
      //   colId: "parent_project",
      //   headerName: "SAMBUNGAN",
      //   field: "parent_project",
      //   width: 130,
      //   suppressSizeToFit: true,
      // },
      {
        colId: "rmk",
        headerName: "RMK",
        field: "rmk.number",
        suppressSizeToFit: true,
      },
      // {
      //   headerName: "BUTIRAN KEMENTERIAN",
      //   field: "ministry.code",
      //   filter: true,
      //   sortable: true,
      // },
      {
        colId: "ministry",
        headerName: "KEMENTERIAN",
        enableRowGroup: true,
        field: "ministry.name",
        sortable: true,
        filter: true,
        filterParams: {
          filterOptions: ["contains"],
          maxNumConditions: 0,
        },
      },
      {
        colId: "department",
        headerName: "JABATAN",
        enableRowGroup: true,
        field: "department.value",
        sortable: true,
        filter: true,
        filterParams: {
          filterOptions: ["contains"],
          maxNumConditions: 0,
        },
      },
      {
        colId: "branch",
        headerName: "CAWANGAN",
        enableRowGroup: true,
        field: "branch.value",
        sortable: true,
        filter: true,
        filterParams: {
          filterOptions: ["contains"],
          maxNumConditions: 0,
        },
      },
      {
        headerName: "NAMA PROJEK UTAMA",
        field: "main_project.name",
        enableRowGroup: true,
        filter: true,
      },
      // {
      //   headerName: "BUTIRAN PROJEK UTAMA",
      //   field: "main_project.code",
      //   enableRowGroup: true,
      //   filter: true,
      // },
      // {
      //   headerName: "KOD PROJEK",
      //   field: "sub_project_full_code",
      //   width: 350,
      //   filter: true,
      // },
      {
        headerName: "NAMA PROJEK",
        field: "name",
        width: 500,
        filter: true,
      },
      {
        headerName: "KETERANGAN PROJEK",
        field: "description",
        width: 800,
      },
      {
        headerName: "KPI PROGRAM dan PROJEK",
        field: "key_performance_indicator",
        width: 500,
      },
      {
        headerName: "JUSTIFIKASI PROJEK",
        field: "justification",
        width: 500,
      },
      {
        headerName:
          "MATLAMAT PEMBANGUNAN MAMPAN SUSTAINABLE DEVELOPMENT GOAL (SDG)",
        cellRenderer: ({ data }) => {
          const sdg = data?.sustainable_development_goals || null;
          return (
            <div>
              <Row className=''>
                {sdg?.map((item, idx) => {
                  return (
                    <Col key={idx} className='mb-3'>
                      <div
                        style={{
                          height: "auto",
                          width: 60,
                        }}
                      >
                        <img
                          className='mb-1 w-100 h-100'
                          alt='image_not_found'
                          src={goalIcons[item]}
                        />
                      </div>
                    </Col>
                  );
                })}
              </Row>
            </div>
          );
        },
      },
      {
        headerName: "BIDANG KEUTAMAAN",
        field: "main_sector.name",
        enableRowGroup: true,
        filter: true,
      },
      {
        headerName: "KETERANGAN BIDANG KEUTAMAAN",
        width: 700,
        cellRenderer: ({ data }) => {
          const items = data?.strategic_directions || null;

          if (items) {
            return (
              <ul>
                {items.map((obj, idx) => {
                  return <li key={idx}>{obj.data?.name}</li>;
                })}
              </ul>
            );
          } else {
            return null;
          }
        },
        valueGetter: (params) => {
          return params.data?.strategic_directions
            ? params.data.strategic_directions
                .map((obj) => `• ${obj.data?.name}`)
                .join("\n")
            : "";
        },
      },
      // {
      //   headerName: "FAEDAH PROJEK",
      //   field: "benefits",
      //   width: 500,
      // },
      {
        headerName: "KEUTAMAAN PROJEK",
        field: "priority",
        enableRowGroup: true,
        filter: true,
        sortable: true,
      },
      {
        headerName: "KATEGORI PROJEK",
        field: "project_category.code",
        enableRowGroup: true,
        filter: true,
        sortable: true,
      },
      // {
      //   headerName: "SEKSYEN UPEN",
      //   field: "upen_section.code",
      //   enableRowGroup: true,
      //   filter: true,
      // },
      {
        headerName: "JENIS PROJEK",
        field: "project_type.name",
        enableRowGroup: true,
        filter: true,
      },
      {
        headerName: "MAKLUMAT KEWANGAN",
        children: [
          {
            headerName: "KOS DIPOHON",
            cellClass: "ag-right-aligned-cell",
            valueGetter: ({ data }) => {
              const costRequired = data?.financial_details
                ?.flatMap((obj) => obj.financial_detail_stages)
                ?.filter((obj) => obj.stages === "4" && obj.status === "2");

              const cost = costRequired?.reduce(
                (acc, obj) => (acc += parseFloat(obj.cost_required)),
                0,
              );
              return numeral(cost).format("0,0.00");
            },
            aggFunc: "jumlah",
          },
          {
            headerName: "SILING DIPOHON",
            cellClass: "ag-right-aligned-cell",
            valueGetter: ({ data }) => {
              const ceilingRequired = data?.financial_details
                ?.flatMap((obj) => obj.financial_detail_stages)
                ?.filter((obj) => obj.stages === "4" && obj.status === "2");

              const ceiling = ceilingRequired?.reduce(
                (acc, obj) => (acc += parseFloat(obj.ceiling)),
                0,
              );
              return numeral(ceiling).format("0,0.00");
            },
            aggFunc: "jumlah",
          },
          {
            headerValueGetter: (params) => {
              return `SILING TAHUN ${selectRmk?.first_year} (RM)`;
            },
            suppressSizeToFit: true,
            width: 250,
            cellClass: "ag-right-aligned-cell",
            valueGetter: ({ data }) => {
              const ceiling = data?.financial_details
                ?.flatMap((obj) => obj.ceiling_flow_details)
                ?.filter((obj) => obj.year === selectRmk.first_year)
                ?.flatMap((obj) => obj.ceiling_flow_detail_stages)
                ?.filter((obj) => obj.stages === "4" && obj.status === "2")
                ?.reduce((acc, obj) => (acc += parseFloat(obj.amount)), 0);

              if (ceiling) {
                return numeral(ceiling).format("0,0.00");
              } else {
                return numeral(0).format("0,0.00");
              }
            },
            aggFunc: "jumlah",
          },
          {
            headerValueGetter: (params) => {
              return `SILING TAHUN ${selectRmk?.second_year} (RM)`;
            },
            suppressSizeToFit: true,
            width: 250,
            cellClass: "ag-right-aligned-cell",
            valueGetter: ({ data }) => {
              const ceiling = data?.financial_details
                ?.flatMap((obj) => obj.ceiling_flow_details)
                ?.filter((obj) => obj.year === selectRmk.second_year)
                ?.flatMap((obj) => obj.ceiling_flow_detail_stages)
                ?.filter((obj) => obj.stages === "4" && obj.status === "2")
                ?.reduce((acc, obj) => (acc += parseFloat(obj.amount)), 0);

              if (ceiling) {
                return numeral(ceiling).format("0,0.00");
              } else {
                return numeral(0).format("0,0.00");
              }
            },
            aggFunc: "jumlah",
          },
          {
            headerValueGetter: (params) => {
              return `SILING TAHUN ${selectRmk?.third_year}`;
            },
            suppressSizeToFit: true,
            width: 250,
            cellClass: "ag-right-aligned-cell",
            valueGetter: ({ data }) => {
              const ceiling = data?.financial_details
                ?.flatMap((obj) => obj.ceiling_flow_details)
                ?.filter((obj) => obj.year === selectRmk.third_year)
                ?.flatMap((obj) => obj.ceiling_flow_detail_stages)
                ?.filter((obj) => obj.stages === "4" && obj.status === "2")
                ?.reduce((acc, obj) => (acc += parseFloat(obj.amount)), 0);

              if (ceiling) {
                return numeral(ceiling).format("0,0.00");
              } else {
                return numeral(0).format("0,0.00");
              }
            },
            aggFunc: "jumlah",
          },
          {
            headerValueGetter: (params) => {
              return `SILING TAHUN ${selectRmk?.fourth_year}`;
            },
            suppressSizeToFit: true,
            width: 250,
            cellClass: "ag-right-aligned-cell",
            valueGetter: ({ data }) => {
              const ceiling = data?.financial_details
                ?.flatMap((obj) => obj.ceiling_flow_details)
                ?.filter((obj) => obj.year === selectRmk.fourth_year)
                ?.flatMap((obj) => obj.ceiling_flow_detail_stages)
                ?.filter((obj) => obj.stages === "4" && obj.status === "2")
                ?.reduce((acc, obj) => (acc += parseFloat(obj.amount)), 0);

              if (ceiling) {
                return numeral(ceiling).format("0,0.00");
              } else {
                return numeral(0).format("0,0.00");
              }
            },
            aggFunc: "jumlah",
          },
          {
            headerValueGetter: (params) => {
              return `SILING TAHUN ${selectRmk?.fifth_year}`;
            },
            suppressSizeToFit: true,
            width: 250,
            cellClass: "ag-right-aligned-cell",
            valueGetter: ({ data }) => {
              const ceiling = data?.financial_details
                ?.flatMap((obj) => obj.ceiling_flow_details)
                ?.filter((obj) => obj.year === selectRmk.fifth_year)
                ?.flatMap((obj) => obj.ceiling_flow_detail_stages)
                ?.filter((obj) => obj.stages === "4" && obj.status === "2")
                ?.reduce((acc, obj) => (acc += parseFloat(obj.amount)), 0);

              if (ceiling) {
                return numeral(ceiling).format("0,0.00");
              } else {
                return numeral(0).format("0,0.00");
              }
            },
            aggFunc: "jumlah",
          },
          {
            headerValueGetter: (params) => {
              return `JUMLAH ALIRAN SILING`;
            },
            suppressSizeToFit: true,
            width: 250,
            cellClass: "ag-right-aligned-cell",
            valueGetter: ({ data }) => {
              const ceiling = data?.financial_details
                ?.flatMap((obj) => obj.ceiling_flow_details)
                ?.flatMap((obj) => obj.ceiling_flow_detail_stages)
                ?.filter((obj) => obj.stages === "4" && obj.status === "2")
                ?.reduce((acc, obj) => (acc += parseFloat(obj.amount)), 0);

              return numeral(ceiling).format("0,0.00");
            },
            aggFunc: "jumlah",
          },
          {
            colId: "funding_sources",
            headerName: "SUMBER PEMBIAYAAN",
            cellRenderer: ({ data }) => {
              const source = data?.funding_sources || [];

              if (source.length > 0) {
                const mapping = {
                  1: "Kumpulan Wang Negeri",
                  2: "Pinjaman Persekutuan",
                  3: "Bayaran Balik Persekutuan",
                };

                // Map numbers to text and join them with ", "
                const text = source
                  .map((num) => mapping[num])
                  .filter(Boolean)
                  .join(", ");

                return <span>{text}</span>;
              } else {
                return <span>-</span>; // Show "-" when there's no funding source
              }
            },
            valueGetter: (params) => {
              const source = params.data?.funding_sources || [];
              const mapping = {
                1: "Kumpulan Wang Negeri",
                2: "Pinjaman Persekutuan",
                3: "Bayaran Balik Persekutuan",
              };
              return (
                source
                  .map((num) => mapping[num])
                  .filter(Boolean)
                  .join(", ") || "-"
              );
            },
          },
        ],
      },
      {
        headerName: "MAKLUMAT SKOP MENGIKUT TAHUN",
        children: [
          {
            headerValueGetter: (params) => {
              return `TAHUN ${selectRmk?.first_year}`;
            },
            width: 700,
            cellRenderer: ({ data }) => {
              const financialDetails = data?.financial_details || null;
              const componentByYear = financialDetails;

              const groupedData = financialDetails?.reduce((acc, item) => {
                item.ceiling_flow_details.forEach((detail) => {
                  const year = detail.year;
                  if (!acc[year]) {
                    acc[year] = [];
                  }
                  acc[year].push({
                    ...item,
                    ceiling_flow_details: [detail],
                  });
                });
                return acc;
              }, {});

              const currentYearData =
                groupedData?.[selectRmk?.first_year] || null;

              const totalCeilingAmount = currentYearData
                ?.flatMap((obj) => obj.ceiling_flow_details)
                .reduce((acc, obj) => acc + parseFloat(obj.amount), 0);

              if (currentYearData) {
                return (
                  <Table size='sm' bordered className='mb-0 pb-0'>
                    <thead className='text-uppercase' style={{ fontSize: 12 }}>
                      <tr>
                        <th>Skop Projek</th>
                        <th>Siling (RM)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentYearData?.map((item, idx) => {
                        const ceilingAmount = item?.ceiling_flow_details?.find(
                          (obj) => obj.year === selectRmk.first_year,
                        );
                        return (
                          <tr key={idx}>
                            <td width={500}>{item.cost_description}</td>
                            <td className='text-end'>
                              {numeral(ceilingAmount.amount).format("0,0.00")}
                            </td>
                          </tr>
                        );
                      })}
                      <tr>
                        <td className='text-end'>Jumlah</td>
                        <td className='text-end'>
                          {numeral(totalCeilingAmount).format("0,0.00")}
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                );
              } else {
                return null;
              }
            },
            valueGetter: (params) => {
              const financialDetails = params.data?.financial_details || [];

              // Filter only relevant year's data
              const currentYearData = financialDetails
                .map((item) => {
                  const ceilingDetail = item.ceiling_flow_details?.find(
                    (detail) => detail.year === selectRmk.first_year,
                  );
                  if (ceilingDetail) {
                    return {
                      description: item.cost_description,
                      amount: ceilingDetail.amount,
                    };
                  }
                  return null;
                })
                .filter(Boolean); // Remove null values

              return currentYearData
                .map(
                  (item, idx) =>
                    `${idx + 1}. ${item.description}: RM ${numeral(
                      item.amount,
                    ).format("0,0.00")}`,
                )
                .join("\n"); // Numbered format for Excel
            },
          },
          {
            headerValueGetter: (params) => {
              return `TAHUN ${selectRmk?.second_year}`;
            },
            width: 700,
            cellRenderer: ({ data }) => {
              const financialDetails = data?.financial_details || null;
              const componentByYear = financialDetails;

              const groupedData = financialDetails?.reduce((acc, item) => {
                item.ceiling_flow_details.forEach((detail) => {
                  const year = detail.year;
                  if (!acc[year]) {
                    acc[year] = [];
                  }
                  acc[year].push({
                    ...item,
                    ceiling_flow_details: [detail],
                  });
                });
                return acc;
              }, {});

              const currentYearData =
                groupedData?.[selectRmk?.second_year] || null;

              const totalCeilingAmount = currentYearData
                ?.flatMap((obj) => obj.ceiling_flow_details)
                .reduce((acc, obj) => acc + parseFloat(obj.amount), 0);

              if (currentYearData) {
                return (
                  <Table size='sm' bordered className='mb-0 pb-0'>
                    <thead className='text-uppercase' style={{ fontSize: 12 }}>
                      <tr>
                        <th>Skop Projek</th>
                        <th>Siling (RM)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentYearData?.map((item, idx) => {
                        const ceilingAmount = item?.ceiling_flow_details?.find(
                          (obj) => obj.year === selectRmk.second_year,
                        );
                        return (
                          <tr key={idx}>
                            <td width={500}>{item.cost_description}</td>
                            <td className='text-end'>
                              {numeral(ceilingAmount.amount).format("0,0.00")}
                            </td>
                          </tr>
                        );
                      })}
                      <tr>
                        <td className='text-end'>Jumlah</td>
                        <td className='text-end'>
                          {numeral(totalCeilingAmount).format("0,0.00")}
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                );
              } else {
                return null;
              }
            },
            valueGetter: (params) => {
              const financialDetails = params.data?.financial_details || [];

              // Filter only relevant year's data
              const currentYearData = financialDetails
                .map((item) => {
                  const ceilingDetail = item.ceiling_flow_details?.find(
                    (detail) => detail.year === selectRmk.second_year,
                  );
                  if (ceilingDetail) {
                    return {
                      description: item.cost_description,
                      amount: ceilingDetail.amount,
                    };
                  }
                  return null;
                })
                .filter(Boolean); // Remove null values

              return currentYearData
                .map(
                  (item, idx) =>
                    `${idx + 1}. ${item.description}: RM ${numeral(
                      item.amount,
                    ).format("0,0.00")}`,
                )
                .join("\n"); // Numbered format for Excel
            },
          },
          {
            headerValueGetter: (params) => {
              return `TAHUN ${selectRmk?.third_year}`;
            },
            width: 700,
            cellRenderer: ({ data }) => {
              const financialDetails = data?.financial_details || null;
              const componentByYear = financialDetails;

              const groupedData = financialDetails?.reduce((acc, item) => {
                item.ceiling_flow_details.forEach((detail) => {
                  const year = detail.year;
                  if (!acc[year]) {
                    acc[year] = [];
                  }
                  acc[year].push({
                    ...item,
                    ceiling_flow_details: [detail],
                  });
                });
                return acc;
              }, {});

              const currentYearData =
                groupedData?.[selectRmk?.third_year] || null;

              const totalCeilingAmount = currentYearData
                ?.flatMap((obj) => obj.ceiling_flow_details)
                .reduce((acc, obj) => acc + parseFloat(obj.amount), 0);

              if (currentYearData) {
                return (
                  <Table size='sm' bordered className='mb-0 pb-0'>
                    <thead className='text-uppercase' style={{ fontSize: 12 }}>
                      <tr>
                        <th>Skop Projek</th>
                        <th>Siling (RM)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentYearData?.map((item, idx) => {
                        const ceilingAmount = item?.ceiling_flow_details?.find(
                          (obj) => obj.year === selectRmk.third_year,
                        );
                        return (
                          <tr key={idx}>
                            <td width={500}>{item.cost_description}</td>
                            <td className='text-end'>
                              {numeral(ceilingAmount.amount).format("0,0.00")}
                            </td>
                          </tr>
                        );
                      })}
                      <tr>
                        <td className='text-end'>Jumlah</td>
                        <td className='text-end'>
                          {numeral(totalCeilingAmount).format("0,0.00")}
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                );
              } else {
                return null;
              }
            },
            valueGetter: (params) => {
              const financialDetails = params.data?.financial_details || [];

              // Filter only relevant year's data
              const currentYearData = financialDetails
                .map((item) => {
                  const ceilingDetail = item.ceiling_flow_details?.find(
                    (detail) => detail.year === selectRmk.third_year,
                  );
                  if (ceilingDetail) {
                    return {
                      description: item.cost_description,
                      amount: ceilingDetail.amount,
                    };
                  }
                  return null;
                })
                .filter(Boolean); // Remove null values

              return currentYearData
                .map(
                  (item, idx) =>
                    `${idx + 1}. ${item.description}: RM ${numeral(
                      item.amount,
                    ).format("0,0.00")}`,
                )
                .join("\n"); // Numbered format for Excel
            },
          },
          {
            headerValueGetter: (params) => {
              return `TAHUN ${selectRmk?.fourth_year}`;
            },
            width: 700,
            cellRenderer: ({ data }) => {
              const financialDetails = data?.financial_details || null;
              const componentByYear = financialDetails;

              const groupedData = financialDetails?.reduce((acc, item) => {
                item.ceiling_flow_details.forEach((detail) => {
                  const year = detail.year;
                  if (!acc[year]) {
                    acc[year] = [];
                  }
                  acc[year].push({
                    ...item,
                    ceiling_flow_details: [detail],
                  });
                });
                return acc;
              }, {});

              const currentYearData =
                groupedData?.[selectRmk?.fourth_year] || null;

              const totalCeilingAmount = currentYearData
                ?.flatMap((obj) => obj.ceiling_flow_details)
                .reduce((acc, obj) => acc + parseFloat(obj.amount), 0);

              if (currentYearData) {
                return (
                  <Table size='sm' bordered className='mb-0 pb-0'>
                    <thead className='text-uppercase' style={{ fontSize: 12 }}>
                      <tr>
                        <th>Skop Projek</th>
                        <th>Siling (RM)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentYearData?.map((item, idx) => {
                        const ceilingAmount = item?.ceiling_flow_details?.find(
                          (obj) => obj.year === selectRmk.fourth_year,
                        );
                        return (
                          <tr key={idx}>
                            <td width={500}>{item.cost_description}</td>
                            <td className='text-end'>
                              {numeral(ceilingAmount.amount).format("0,0.00")}
                            </td>
                          </tr>
                        );
                      })}
                      <tr>
                        <td className='text-end'>Jumlah</td>
                        <td className='text-end'>
                          {numeral(totalCeilingAmount).format("0,0.00")}
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                );
              } else {
                return null;
              }
            },
            valueGetter: (params) => {
              const financialDetails = params.data?.financial_details || [];

              // Filter only relevant year's data
              const currentYearData = financialDetails
                .map((item) => {
                  const ceilingDetail = item.ceiling_flow_details?.find(
                    (detail) => detail.year === selectRmk.fourth_year,
                  );
                  if (ceilingDetail) {
                    return {
                      description: item.cost_description,
                      amount: ceilingDetail.amount,
                    };
                  }
                  return null;
                })
                .filter(Boolean); // Remove null values

              return currentYearData
                .map(
                  (item, idx) =>
                    `${idx + 1}. ${item.description}: RM ${numeral(
                      item.amount,
                    ).format("0,0.00")}`,
                )
                .join("\n"); // Numbered format for Excel
            },
          },
          {
            headerValueGetter: (params) => {
              return `TAHUN ${selectRmk?.fifth_year}`;
            },
            width: 700,
            cellRenderer: ({ data }) => {
              const financialDetails = data?.financial_details || null;
              const componentByYear = financialDetails;

              const groupedData = financialDetails?.reduce((acc, item) => {
                item.ceiling_flow_details.forEach((detail) => {
                  const year = detail.year;
                  if (!acc[year]) {
                    acc[year] = [];
                  }
                  acc[year].push({
                    ...item,
                    ceiling_flow_details: [detail],
                  });
                });
                return acc;
              }, {});

              const currentYearData =
                groupedData?.[selectRmk?.fifth_year] || null;

              const totalCeilingAmount = currentYearData
                ?.flatMap((obj) => obj.ceiling_flow_details)
                .reduce((acc, obj) => acc + parseFloat(obj.amount), 0);

              if (currentYearData) {
                return (
                  <Table size='sm' bordered className='mb-0 pb-0'>
                    <thead className='text-uppercase' style={{ fontSize: 12 }}>
                      <tr>
                        <th>Skop Projek</th>
                        <th>Siling (RM)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentYearData?.map((item, idx) => {
                        const ceilingAmount = item?.ceiling_flow_details?.find(
                          (obj) => obj.year === selectRmk.fifth_year,
                        );
                        return (
                          <tr key={idx}>
                            <td width={500}>{item.cost_description}</td>
                            <td className='text-end'>
                              {numeral(ceilingAmount.amount).format("0,0.00")}
                            </td>
                          </tr>
                        );
                      })}
                      <tr>
                        <td className='text-end'>Jumlah</td>
                        <td className='text-end'>
                          {numeral(totalCeilingAmount).format("0,0.00")}
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                );
              } else {
                return null;
              }
            },
            valueGetter: (params) => {
              const financialDetails = params.data?.financial_details || [];

              // Filter only relevant year's data
              const currentYearData = financialDetails
                .map((item) => {
                  const ceilingDetail = item.ceiling_flow_details?.find(
                    (detail) => detail.year === selectRmk.fifth_year,
                  );
                  if (ceilingDetail) {
                    return {
                      description: item.cost_description,
                      amount: ceilingDetail.amount,
                    };
                  }
                  return null;
                })
                .filter(Boolean); // Remove null values

              return currentYearData
                .map(
                  (item, idx) =>
                    `${idx + 1}. ${item.description}: RM ${numeral(
                      item.amount,
                    ).format("0,0.00")}`,
                )
                .join("\n"); // Numbered format for Excel
            },
          },
        ],
      },
      {
        headerName: "MAKLUMAT FIZIKAL",
        children: [
          {
            headerName: "SKOP PROJEK",
            width: 700,
            cellRenderer: ({ data }) => {
              return (
                <Table size='sm' bordered className='mb-0 pb-0'>
                  <thead className='text-uppercase' style={{ fontSize: 12 }}>
                    <tr>
                      <th>Skop Projek</th>
                      <th>Siling (RM)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data?.financial_details?.map((item, idx) => {
                      return (
                        <tr key={idx}>
                          <td width={500}>{item.cost_description}</td>
                          <td className='text-end'>
                            {numeral(item.ceiling).format("0,0.00")}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              );
            },
            valueGetter: (params) => {
              const financialDetails = params.data?.financial_details || [];

              return financialDetails
                .map(
                  (item, idx) =>
                    `${idx + 1}. ${item.cost_description}: RM ${numeral(
                      item.ceiling,
                    ).format("0,0.00")}`,
                )
                .join("\n"); // Numbered format for Excel
            },
          },
          {
            headerValueGetter: (params) => {
              return `PERANCANGAN TAHAP PELAKSANAAN ${selectRmk?.short_form}`;
            },
            width: 600,
            cellRenderer: ({ data }) => {
              const levelOfImplementation =
                data?.physical_details?.level_of_implementation || null;

              return (
                <div
                  style={{
                    pointerEvents: "none",
                  }}
                  tabIndex='-1'
                >
                  <EditorNoTools
                    ref={quillRef1}
                    readOnly={true}
                    defaultValue={levelOfImplementation}
                  />
                </div>
              );
            },
            valueGetter: (params) => {
              const content =
                params.data?.physical_details?.level_of_implementation;

              if (!content) return ""; // Return empty if no content

              // Check if the content is an object (Quill Delta format)
              if (typeof content === "object" && content.ops) {
                return content.ops.map((op) => op.insert).join(""); // Extract text from Quill Delta
              }

              // If content is HTML string, extract plain text
              const tempDiv = document.createElement("div");
              tempDiv.innerHTML = content;
              return tempDiv.textContent || tempDiv.innerText || "";
            },
          },
          {
            headerName: "SPESIFIKASI TEKNIKAL",
            width: 600,
            cellRenderer: ({ data }) => {
              const technicalSpecification =
                data?.physical_details?.technical_specification || null;

              return (
                <div
                  style={{
                    pointerEvents: "none",
                  }}
                  tabIndex='-1'
                >
                  <EditorNoTools
                    ref={quillRef2}
                    readOnly={true}
                    defaultValue={technicalSpecification}
                  />
                </div>
              );
            },
            valueGetter: (params) => {
              const content =
                params.data?.physical_details?.technical_specification;

              if (!content) return ""; // Return empty if no content

              // Check if the content is an object (Quill Delta format)
              if (typeof content === "object" && content.ops) {
                return content.ops.map((op) => op.insert).join(""); // Extract text from Quill Delta
              }

              // If content is HTML string, extract plain text
              const tempDiv = document.createElement("div");
              tempDiv.innerHTML = content;
              return tempDiv.textContent || tempDiv.innerText || "";
            },
          },
          {
            headerName: "REKA BENTUK",
            sortable: true,
            enableRowGroup: true,
            cellRenderer: ({ data }) => {
              const projectDesign =
                data?.physical_details?.project_design_status || null;
              return (
                <div>
                  {projectDesign === 1 && "Tidak Diperlukan"}
                  {projectDesign === 2 && "Belum Disediakan"}
                  {projectDesign === 3 && "Telah Disediakan"}
                </div>
              );
            },
            valueGetter: (params) => {
              const projectDesign =
                params.data?.physical_details?.project_design_status;
              if (projectDesign === 1) return "Tidak Diperlukan";
              if (projectDesign === 2) return "Belum Disediakan";
              if (projectDesign === 3) return "Telah Disediakan";
              return "";
            },
          },
          {
            headerName: "TARIKH TENDER (JANGKAAN)",
            sortable: true,
            cellRenderer: ({ data }) => {
              const date = data?.physical_details?.expected_tender_date || null;

              return (
                <div className='d-flex justify-content-center'>
                  {!isEmpty(date) && format(new Date(date), "dd MMM yyyy")}
                </div>
              );
            },
            valueGetter: (params) => {
              const date = params.data?.physical_details?.expected_tender_date;
              return date ? format(new Date(date), "yyyy-MM-dd") : "-";
            },
          },
          {
            headerName: "TARIKH MULA PROJEK (JANGKAAN)",
            sortable: true,
            cellRenderer: ({ data }) => {
              const date = data?.physical_details?.expected_start_date || null;

              return (
                <div className='d-flex justify-content-center'>
                  {!isEmpty(date) && format(new Date(date), "dd MMM yyyy")}
                </div>
              );
            },
            valueGetter: (params) => {
              const date = params.data?.physical_details?.expected_start_date;
              return date ? format(new Date(date), "yyyy-MM-dd") : "-";
            },
          },
          {
            headerName: "TARIKH SIAP PROJEK (JANGKAAN)",
            sortable: true,
            cellRenderer: ({ data }) => {
              const date =
                data?.physical_details?.expected_completion_date || null;

              return (
                <div className='d-flex justify-content-center'>
                  {!isEmpty(date) && format(new Date(date), "dd MMM yyyy")}
                </div>
              );
            },
            valueGetter: (params) => {
              const date =
                params.data?.physical_details?.expected_completion_date;
              return date ? format(new Date(date), "yyyy-MM-dd") : "-";
            },
          },
          {
            headerName: "KELUASAN TAPAK",
            field: "physical_details.site_width",
            valueGetter: (params) => {
              return params.data?.physical_details?.site_width ?? "-";
            },
          },
          {
            headerName:
              "CADANGAN KEGUNAAN TANAH SELARAS DENGAN RANCANGAN TEMPATAN",
            sortable: true,
            enableRowGroup: true,
            cellRenderer: ({ data }) => {
              const plan =
                data?.physical_details?.accordance_with_local_plans || null;

              return <div> {plan ? "Ya" : "Tidak"}</div>;
            },
            valueGetter: (params) => {
              return params.data?.physical_details?.accordance_with_local_plans
                ? "Ya"
                : "Tidak";
            },
          },
          {
            headerName: "CADANGAN BERKAITAN UTILITI",
            sortable: true,
            enableRowGroup: true,
            cellRenderer: ({ data }) => {
              const utility = data?.physical_details?.utility_related || null;

              return <div> {utility ? "Ya" : "Tidak"}</div>;
            },
            valueGetter: (params) => {
              return params.data?.physical_details?.utility_related
                ? "Ya"
                : "Tidak";
            },
          },
          {
            headerName: "DAERAH",
            sortable: true,
            enableRowGroup: true,
            cellRenderer: ({ data }) => {
              const site = data?.site_information;

              return (
                <div className='d-flex justify-content-center'>
                  {site?.daerah?.name}
                </div>
              );
            },
            valueGetter: (params) => {
              return params.data?.site_information?.daerah?.name || "-";
            },
          },

          {
            headerName: "PARLIMEN",
            sortable: true,
            enableRowGroup: true,
            cellRenderer: ({ data }) => {
              const site = data?.site_information;
              return (
                <div className='d-flex justify-content-center'>
                  {site?.parliament?.name} - {site?.parliament?.code}
                </div>
              );
            },
            valueGetter: (params) => {
              const parliament = params.data?.site_information?.parliament;
              return parliament?.name
                ? `${parliament.name} - ${parliament.code}`
                : "-";
            },
          },
          {
            headerName: "DUN",
            sortable: true,
            enableRowGroup: true,
            cellRenderer: ({ data }) => {
              const site = data?.site_information;
              return (
                <div className='d-flex justify-content-center'>
                  {site?.state_legislative_assembly?.name} -{" "}
                  {site?.state_legislative_assembly?.code}
                </div>
              );
            },
            valueGetter: (params) => {
              return (
                params.data?.site_information?.state_legislative_assembly
                  ?.name || "-"
              );
            },
          },
          {
            headerName: "KAWASAN",
            sortable: true,
            enableRowGroup: true,
            field: "site_information.area",
          },
          {
            headerName: "KAWASAN SENSITIF ALAM SEKITAR",
            width: 600,
            cellRenderer: ({ data }) => {
              const eia =
                data?.physical_details?.environmental_impact_areas || null;

              return (
                <div
                  style={{
                    pointerEvents: "none",
                  }}
                  tabIndex='-1'
                >
                  <EditorNoTools
                    ref={quillRef3}
                    readOnly={true}
                    defaultValue={eia}
                  />
                </div>
              );
            },
            valueGetter: (params) => {
              const content =
                params.data?.physical_details?.environmental_impact_areas;

              if (!content) return ""; // Return empty if no content

              // Check if the content is an object (Quill Delta format)
              if (typeof content === "object" && content.ops) {
                return content.ops.map((op) => op.insert).join(""); // Extract text from Quill Delta
              }

              // If content is HTML string, extract plain text
              const tempDiv = document.createElement("div");
              tempDiv.innerHTML = content;
              return tempDiv.textContent || tempDiv.innerText || "";
            },
          },
          {
            headerName: "SYARAT KEGUNAAN TANAH/SEKATAN",
            width: 600,
            cellRenderer: ({ data }) => {
              const landUse = data?.physical_details?.land_use || null;

              return (
                <div
                  style={{
                    pointerEvents: "none",
                  }}
                  tabIndex='-1'
                >
                  <EditorNoTools
                    ref={quillRef4}
                    readOnly={true}
                    defaultValue={landUse}
                  />
                </div>
              );
            },
            valueGetter: (params) => {
              const content = params.data?.physical_details?.land_use;

              if (!content) return ""; // Return empty if no content

              // Check if the content is an object (Quill Delta format)
              if (typeof content === "object" && content.ops) {
                return content.ops.map((op) => op.insert).join(""); // Extract text from Quill Delta
              }

              // If content is HTML string, extract plain text
              const tempDiv = document.createElement("div");
              tempDiv.innerHTML = content;
              return tempDiv.textContent || tempDiv.innerText || "";
            },
          },
          {
            colId: "geran",
            headerName: "GERAN",
            field: "site_information.geran",
          },
          {
            colId: "reference_no",
            headerName: "NO. FAIl RUJUKAN MyeTAPP/KKPTG/No. HAK MILIK",
            field: "site_information.reference_no",
          },
        ],
      },
      {
        headerName: "MAKLUMAT LAIN",
        children: [
          {
            colId: "human_resources",
            headerName: "KEPERLUAN SUMBER MANUSIA",
            width: 600,
            cellRenderer: ({ data }) => {
              const manPower = data?.human_resources_requirements || null;
              return (
                <Table size='sm' bordered className='mb-0 pb-0'>
                  <thead className='text-uppercase' style={{ fontSize: 12 }}>
                    <tr>
                      <th>Bilangan Pertambahan Perjawatan</th>
                      <th>Nama Jawatan</th>
                      <th>Gred Jawatan</th>
                      <th>Justifikasi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {isEmpty(manPower) && (
                      <tr>
                        <td colSpan={4} className='text-center'>
                          Tiada tambahan staff
                        </td>
                      </tr>
                    )}
                    {manPower?.map((item, idx) => {
                      return (
                        <tr key={idx}>
                          <td className='text-center'>
                            {item.number_of_staff}
                          </td>
                          <td>{item.position_name}</td>
                          <td className='text-center'>{item.position_gred}</td>
                          <td className='text-center' colSpan={3}>
                            {item.justification}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              );
            },
          },
          {
            colId: "section_upen",
            headerName: "SEKSYEN UPEN",
            field: "upen_section.code",
          },
          {
            colId: "applicant_agency",
            headerName: "AGENSI PEMOHON",
            field: "applicant_agency.value",
          },
          {
            headerName: "AGENSI PEMOHON",
            field: "owner_agency.value",
          },
          {
            headerName: "AGENSI BERTANGGUNGJAWAB (KEWANGAN)",
            field: "financially_responsible_agency.value",
          },
          {
            headerName: "AGENSI PELAKSANA",
            field: "executing_agency.value",
          },
          {
            headerName: "MAKLUMAT TAMBAHAN",
            width: 600,
            cellRenderer: ({ data }) => {
              const additionalInformation =
                data?.additional_information || null;

              return (
                <div
                  style={{
                    pointerEvents: "none",
                  }}
                  tabIndex='-1'
                >
                  <EditorNoTools
                    ref={quillRef5}
                    readOnly={true}
                    defaultValue={additionalInformation}
                  />
                </div>
              );
            },
            valueGetter: (params) => {
              const content = params.data?.additional_information;

              if (!content) return ""; // Return empty if no content

              // Check if the content is an object (Quill Delta format)
              if (typeof content === "object" && content.ops) {
                return content.ops.map((op) => op.insert).join(""); // Extract text from Quill Delta
              }

              // If content is HTML string, extract plain text
              const tempDiv = document.createElement("div");
              tempDiv.innerHTML = content;
              return tempDiv.textContent || tempDiv.innerText || "";
            },
          },
        ],
      },

      // {
      //   headerValueGetter: (params) => {
      //     return `MAKLUMAT SILING ${selectRmk?.label}`;
      //   },
      //   children: [
      //     {
      //       headerValueGetter: (params) => {
      //         return `ASAL (RM)`;
      //       },
      //       cellClass: "ag-right-aligned-cell",
      //       valueGetter: ({ data }) => {
      //         const ceiling = data?.financial_details?.reduce(
      //           (acc, obj) => (acc += parseFloat(obj.ceiling)),
      //           0,
      //         );
      //         return numeral(ceiling).format("0,0.00");
      //       },
      //       aggFunc: "jumlah",
      //     },
      //     {
      //       headerValueGetter: (params) => {
      //         return `DISEMAK (RM)`;
      //       },
      //       suppressSizeToFit: true,
      //       width: 350,
      //       sortable: true,
      //       sort: "desc",
      //       cellClass: "ag-right-aligned-cell",
      //       comparator: (valueA, valueB, nodeA, nodeB, isDescending) => {
      //         const getLatestProgress = (node) => {
      //           const revisedCeiling = node?.data?.financial_details?.reduce(
      //             (acc, item) => (acc += parseFloat(item.revised_ceiling || 0)),
      //             0,
      //           );

      //           return revisedCeiling;
      //         };

      //         const rowValueA = getLatestProgress(nodeA);
      //         const rowValueB = getLatestProgress(nodeB);

      //         if (rowValueA === rowValueB) return 0;
      //         return rowValueA > rowValueB ? 1 : -1;
      //       },
      //       valueGetter: ({ data }) => {
      //         const revisedCeiling = data?.financial_details?.reduce(
      //           (acc, item) => (acc += parseFloat(item.revised_ceiling || 0)),
      //           0,
      //         );
      //         return numeral(revisedCeiling).format("0,0.00");
      //       },
      //       aggFunc: "jumlah",
      //     },
      //   ],
      // },

      // {
      //   headerName: "PERAKUAN UNIT PERANCANG EKONOMI (UPEN)",
      //   children: [
      //     {
      //       headerName: "KOS ASAL",
      //       cellClass: "ag-right-aligned-cell",
      //       valueGetter: ({ data }) => {
      //         const cost = data?.financial_details?.reduce(
      //           (acc, obj) => (acc += parseFloat(obj.cost_required)),
      //           0,
      //         );
      //         return numeral(cost).format("0,0.00");
      //       },
      //       aggFunc: "jumlah",
      //     },
      //     {
      //       headerValueGetter: (params) => {
      //         return `SILING TAHUN ${selectRmk?.first_year} (RM)`;
      //       },
      //       suppressSizeToFit: true,
      //       width: 250,
      //       cellClass: "ag-right-aligned-cell",
      //       valueGetter: ({ data }) => {
      //         const ceiling =
      //           data?.financial_details
      //             ?.flatMap((obj) => obj.ceiling_flow_details)
      //             ?.find((obj) => obj.year === selectRmk.first_year)
      //             ?.ceiling_flow_detail_stages?.find(
      //               (obj) => obj.stages === "3" && obj.status === "4",
      //             ) || null;

      //         if (ceiling) {
      //           return numeral(ceiling?.amount).format("0,0.00");
      //         } else {
      //           return "-";
      //         }
      //       },
      //       aggFunc: "jumlah",
      //     },
      //     {
      //       headerValueGetter: (params) => {
      //         return `SILING ASAL TAHUN ${selectRmk?.second_year}`;
      //       },
      //       suppressSizeToFit: true,
      //       width: 250,
      //       cellClass: "ag-right-aligned-cell",
      //       valueGetter: ({ data }) => {
      //         const ceiling =
      //           data?.financial_details
      //             ?.flatMap((obj) => obj.ceiling_flow_details)
      //             ?.filter((obj) => obj.year === selectRmk.second_year)
      //             ?.ceiling_flow_detail_stages?.find(
      //               (obj) => obj.stages === "3" && obj.status === "4",
      //             ) || null;

      //         if (ceiling) {
      //           return numeral(ceiling?.amount).format("0,0.00");
      //         } else {
      //           return "-";
      //         }
      //       },
      //       aggFunc: "jumlah",
      //     },
      //     {
      //       headerValueGetter: (params) => {
      //         return `SILING ASAL TAHUN ${selectRmk?.third_year}`;
      //       },
      //       suppressSizeToFit: true,
      //       width: 250,
      //       cellClass: "ag-right-aligned-cell",
      //       valueGetter: ({ data }) => {
      //         const ceiling =
      //           data?.financial_details
      //             ?.flatMap((obj) => obj.ceiling_flow_details)
      //             ?.filter((obj) => obj.year === selectRmk.third_year)
      //             ?.ceiling_flow_detail_stages?.find(
      //               (obj) => obj.stages === "3" && obj.status === "4",
      //             ) || null;

      //         if (ceiling) {
      //           return numeral(ceiling?.amount).format("0,0.00");
      //         } else {
      //           return "-";
      //         }
      //       },
      //       aggFunc: "jumlah",
      //     },
      //     {
      //       headerValueGetter: (params) => {
      //         return `SILING ASAL TAHUN ${selectRmk?.fourth_year}`;
      //       },
      //       suppressSizeToFit: true,
      //       width: 250,
      //       cellClass: "ag-right-aligned-cell",
      //       valueGetter: ({ data }) => {
      //         const ceiling =
      //           data?.financial_details
      //             ?.flatMap((obj) => obj.ceiling_flow_details)
      //             ?.filter((obj) => obj.year === selectRmk.fourth_year)
      //             ?.ceiling_flow_detail_stages?.find(
      //               (obj) => obj.stages === "3" && obj.status === "4",
      //             ) || null;

      //         if (ceiling) {
      //           return numeral(ceiling?.amount).format("0,0.00");
      //         } else {
      //           return "-";
      //         }
      //       },
      //       aggFunc: "jumlah",
      //     },
      //     {
      //       headerValueGetter: (params) => {
      //         return `SILING ASAL TAHUN ${selectRmk?.fifth_year}`;
      //       },
      //       suppressSizeToFit: true,
      //       width: 250,
      //       cellClass: "ag-right-aligned-cell",
      //       valueGetter: ({ data }) => {
      //         const ceiling =
      //           data?.financial_details
      //             ?.flatMap((obj) => obj.ceiling_flow_details)
      //             ?.filter((obj) => obj.year === selectRmk.fifth_year)
      //             ?.ceiling_flow_detail_stages?.find(
      //               (obj) => obj.stages === "3" && obj.status === "4",
      //             ) || null;

      //         if (ceiling) {
      //           return numeral(ceiling?.amount).format("0,0.00");
      //         } else {
      //           return "-";
      //         }
      //       },
      //       aggFunc: "jumlah",
      //     },
      //     {
      //       headerValueGetter: (params) => {
      //         return `JUMLAH SILING`;
      //       },
      //       suppressSizeToFit: true,
      //       width: 250,
      //       cellClass: "ag-right-aligned-cell",
      //       valueGetter: ({ data }) => {
      //         const ceiling = data?.financial_details
      //           ?.flatMap((obj) => obj.ceiling_flow_details)
      //           ?.reduce((acc, obj) => (acc += parseFloat(obj.amount)), 0);
      //         return numeral(ceiling).format("0,0.00");
      //       },
      //       aggFunc: "jumlah",
      //     },
      //   ],
      // },
    ];
  }, [selectRmk]);

  const localeText = useMemo(() => {
    return AG_GRID_LOCALE_MS;
  }, []);

  const defaultColDef = useMemo(() => {
    return {
      cellDataType: false,
      minWidth: 100,
      suppressSizeToFit: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      wrapText: true,
      autoHeight: true,
      resizable: true,
      cellStyle: {
        lineHeight: "25px",
        wordBreak: "normal",
      },
    };
  }, []);

  const autoGroupColumnDef = useMemo(() => {
    return {
      minWidth: 200,
      pinned: "left",
    };
  }, []);

  const aggFuncs = useMemo(() => {
    return {
      // this overrides the grids built in sum function
      jumlah: ({ values }) => {
        const total = values
          .map((val) => numeral(val).value())
          .reduce((acc, val) => (acc += parseFloat(val)), 0);
        return numeral(total).format("0,0.00");
      },
      // this adds another function called 'abc'
      // 123: oneTwoThreeFunc,
      // and again xyz
      // xyz: xyzFunc,
    };
  }, []);

  useEffect(() => {
    if (gridRef.current && gridRef.current.api) {
      // Example of using the API
      gridRef.current.api.sizeColumnsToFit();
    }
  }, [rowData]);

  return (
    <React.Fragment>
      <div style={containerStyle}>
        <div className='grid-wrapper'>
          <div style={gridStyle} className={"ag-theme-alpine"}>
            <AgGridReact
              // localeText={localeText}
              ref={gridRef}
              rowData={rowData}
              aggFuncs={aggFuncs}
              columnDefs={colDefs}
              defaultColDef={defaultColDef}
              sideBar={true}
              onGridReady={onGridReady}
              groupTotalRow={"bottom"}
              autoGroupColumnDef={autoGroupColumnDef}
              suppressDragLeaveHidesColumns={true}
              suppressMakeColumnVisibleAfterUnGroup={true}
              suppressRowGroupHidesColumns={true}
              rowGroupPanelShow={"always"}
              groupIncludeFooter={true}
              groupIncludeTotalFooter={true}
              suppressAggFuncInHeader={true}
              onColumnMoved={saveColumnState}
              onColumnVisible={saveColumnState}
              onColumnPinned={saveColumnState}
              onColumnResized={saveColumnState}
              onSortChanged={saveColumnState}
              onColumnRowGroupChanged={saveColumnState}
            />
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default AnalysisTable;
